import React from 'react'
import { Download, FileSpreadsheet, RotateCcw } from 'lucide-react'
import { fileDownloadUrl, GeneratedFile } from '../../api/chatService'

interface FileBlockProps {
  files: GeneratedFile[]
  chatId?: string
  messageId?: string
}

/** "4,812 rows · 214 KB" — enough to know what you are about to open. */
const describe = (file: GeneratedFile): string => {
  const parts: string[] = []
  if (typeof file.row_count === 'number') {
    parts.push(`${file.row_count.toLocaleString('en-GB')} rows`)
  }
  if (typeof file.byte_size === 'number') {
    const kb = file.byte_size / 1024
    parts.push(kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.ceil(kb)} KB`)
  }
  return parts.join(' · ')
}

/**
 * Files the agent generated for this message.
 *
 * Each card is a plain link to an app endpoint, never to storage: that endpoint
 * checks the chat belongs to you and redirects to a link valid for minutes, so
 * clicking the same card in a year still works. The save is driven by the
 * Content-Disposition set on the blob rather than by `download`, which browsers
 * ignore once a redirect crosses to another origin.
 *
 * Files from before exports were stored have no `blob_path`; those are shown as
 * no longer available rather than being dropped, so an answer that promises a
 * CSV is never left sitting above nothing.
 */
const FileBlock: React.FC<FileBlockProps> = ({ files, chatId, messageId }) => {
  if (!files || files.length === 0) return null

  return (
    <div className="file-block">
      {files.map((file, index) => {
        const downloadable = Boolean(file.blob_path && chatId && messageId)
        const key = `${file.filename}-${index}`

        if (!downloadable) {
          return (
            <div
              key={key}
              className="file-block__item file-block__item--expired"
              role="note"
            >
              <span className="file-block__icon">
                <RotateCcw size={18} />
              </span>
              <span className="file-block__text">
                <span className="file-block__name">{file.filename}</span>
                <span className="file-block__meta">
                  This download is no longer available. Ask BreadGPT to prepare
                  it again.
                </span>
              </span>
            </div>
          )
        }

        return (
          <a
            key={key}
            className="file-block__item"
            href={fileDownloadUrl(chatId as string, messageId as string, file.filename)}
            download={file.filename}
            title={`Download ${file.filename}`}
          >
            <span className="file-block__icon">
              <FileSpreadsheet size={18} />
            </span>
            <span className="file-block__text">
              <span className="file-block__name">{file.filename}</span>
              <span className="file-block__meta">{describe(file)}</span>
            </span>
            <span className="file-block__action">
              <Download size={16} />
            </span>
          </a>
        )
      })}
    </div>
  )
}

export default FileBlock
